/**
 * custom-map.js – Mapkraft Product Map Builder (Mapbox GL JS v3 + Geocoder)
 *
 * Writes line-item properties:
 *  - properties[Orientation]
 *  - properties[Map center]
 *  - properties[Map bounds]
 *  - properties[B bounds]
 *  - properties[Marker coordinates]
 *  - properties[Map zoom]
 *  - properties[Thumbnail URL]
 *
 * Requires in theme.liquid:
 *  - mapbox-gl-js v3.x CSS + JS
 *  - mapbox-gl-geocoder CSS + JS (optional but recommended)
 *  - this file (custom-map.js)
 */

(function () {
  "use strict";

  // ----------------------------
  // Helpers
  // ----------------------------

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function round(n, digits) {
    var p = Math.pow(10, digits || 6);
    return Math.round(n * p) / p;
  }

  function safeParseFloat(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    var R = 6371;
    var toRad = function (v) { return (v * Math.PI) / 180; };

    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);

    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function debounce(fn, wait) {
    var t = null;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Mapbox style can be overridden via data-map-style on the section element.
  // Default: your style
  var DEFAULT_STYLE = "mapbox://styles/bolota/cmiu9efl6001x01r16dslcavw";

  // ----------------------------
  // Bootstrap
  // ----------------------------

  function initAll() {
    var sections = document.querySelectorAll(".product-map-builder[data-mapbox-token]");
    if (!sections || !sections.length) return;
    sections.forEach(initInstance);
  }

  function initInstance(sectionEl) {
    if (sectionEl._mapInitialized) return;
    sectionEl._mapInitialized = true;

    // Mapbox GL required
    if (typeof mapboxgl === "undefined") return;

    var token = sectionEl.getAttribute("data-mapbox-token");
    if (!token) return;
    mapboxgl.accessToken = token;

    var mapStyle = sectionEl.getAttribute("data-map-style") || DEFAULT_STYLE;

    var mapContainer = sectionEl.querySelector(".product-map-builder__map");
    var wrapper = sectionEl.querySelector(".product-map-builder__map-wrapper");
    var rectEl = sectionEl.querySelector(".map-selection-rect");
    if (!mapContainer || !wrapper || !rectEl) return;

    var labelEl = rectEl.querySelector(".map-selection-rect__label");
    var toggleBtn = sectionEl.querySelector(".product-map-builder__orientation-toggle");

    // Hidden inputs (line item properties)
var hiddenOrientation = sectionEl.querySelector('[name="properties[_Orientation]"]');
var hiddenCenter = sectionEl.querySelector('[name="properties[_Map center]"]');
var hiddenBounds = sectionEl.querySelector('[name="properties[_Map bounds]"]');
var hiddenBlosm = sectionEl.querySelector('[name="properties[_B bounds]"]');
var hiddenMarker = sectionEl.querySelector('[name="properties[_Marker coordinates]"]');
var hiddenZoom = sectionEl.querySelector('[name="properties[_Map zoom]"]');
var hiddenThumb = sectionEl.querySelector('[name="properties[_Thumbnail URL]"]');

    // Initial values
    var lat = safeParseFloat(sectionEl.getAttribute("data-initial-lat"), 41.14961);
    var lng = safeParseFloat(sectionEl.getAttribute("data-initial-lng"), -8.61099);

    var orientation = (sectionEl.getAttribute("data-initial-orientation") || "vertical").toLowerCase();
    orientation = (orientation === "horizontal") ? "horizontal" : "vertical";

    // Validation constraints
    var minKm = safeParseFloat(sectionEl.getAttribute("data-min-main-side-km"), 0.5);
    var maxKm = safeParseFloat(sectionEl.getAttribute("data-max-main-side-km"), 2.0);

    // Create map
    var map = new mapboxgl.Map({
      container: mapContainer,
      style: mapStyle,
      center: [lng, lat],
      zoom: 14,
      preserveDrawingBuffer: true // keeps canvas available (optional)
    });

    mapContainer._mapInstance = map;

    // NEVER allow rotation (all devices)
    try { map.dragRotate.disable(); } catch (e) {}
    try { map.touchZoomRotate.disableRotation(); } catch (e) {}
    try { map.keyboard.disableRotation(); } catch (e) {}

    // Debug hooks (safe)
    map.on("error", function (e) {
      // eslint-disable-next-line no-console
      console.log("MAPBOX ERROR:", e && e.error ? e.error : e);
    });

    map.on("load", function () {
      try {
        var s = map.getStyle && map.getStyle();
        // eslint-disable-next-line no-console
        console.log("STYLE LOADED NAME:", s && s.name ? s.name : "(no name)");
        // eslint-disable-next-line no-console
        console.log("STYLE LOADED SPRITE:", s && s.sprite ? s.sprite : "(no sprite)");
      } catch (_) {}
    });

    // Marker overlay element (PNG marker)
    var markerEl = document.createElement("div");
    markerEl.className = "product-map-builder__marker";
    markerEl.style.display = "none";
    markerEl.style.position = "absolute";
    markerEl.style.width = "36px";
    markerEl.style.height = "36px";
    markerEl.style.backgroundImage = "url('https://cdn.shopify.com/s/files/1/0488/7006/4281/files/Asset_2.png?v=1763655234')";
    markerEl.style.backgroundRepeat = "no-repeat";
    markerEl.style.backgroundSize = "contain";
    markerEl.style.backgroundPosition = "center";
    markerEl.style.pointerEvents = "none";
    markerEl.style.zIndex = "5";
    // anchor bottom-center like a map pin
    markerEl.style.transform = "translate(-50%, -100%)";

    wrapper.style.position = wrapper.style.position || "relative";
    wrapper.appendChild(markerEl);

    function getRectBounds() {
      var canvas = map.getCanvas();
      var canvasRect = canvas.getBoundingClientRect();

      var rectW = rectEl.offsetWidth;
      var rectH = rectEl.offsetHeight;

      var cx = canvasRect.width / 2;
      var cy = canvasRect.height / 2;

      var left = cx - rectW / 2;
      var right = cx + rectW / 2;
      var top = cy - rectH / 2;
      var bottom = cy + rectH / 2;

      var nw = map.unproject([left, top]);
      var ne = map.unproject([right, top]);
      var sw = map.unproject([left, bottom]);
      var se = map.unproject([right, bottom]);

      var minLat = Math.min(nw.lat, ne.lat, sw.lat, se.lat);
      var maxLat = Math.max(nw.lat, ne.lat, sw.lat, se.lat);
      var minLng = Math.min(nw.lng, ne.lng, sw.lng, se.lng);
      var maxLng = Math.max(nw.lng, ne.lng, sw.lng, se.lng);

      return { minLat: minLat, maxLat: maxLat, minLng: minLng, maxLng: maxLng };
    }

    // ----------------------------
    // Marker rules: ONLY inside rectangle
    // ----------------------------

    function isLngInRange(lngVal, minLngVal, maxLngVal) {
      // Handle antimeridian (rare but safe)
      if (minLngVal <= maxLngVal) return lngVal >= minLngVal && lngVal <= maxLngVal;
      return lngVal >= minLngVal || lngVal <= maxLngVal;
    }

    function isLngLatInsideRect(lngLat) {
      var b = getRectBounds();
      var latOk = lngLat.lat >= b.minLat && lngLat.lat <= b.maxLat;
      var lngOk = isLngInRange(lngLat.lng, b.minLng, b.maxLng);
      return latOk && lngOk;
    }

    function clearMarker() {
      if (hiddenMarker) hiddenMarker.value = "";
      markerEl.style.display = "none";
      buildThumbnailDebounced();
    }

    function ensureMarkerWithinRect() {
      if (!hiddenMarker || !hiddenMarker.value) return;

      var parts = hiddenMarker.value.split(",");
      if (parts.length !== 2) {
        clearMarker();
        return;
      }

      var mLat = parseFloat(parts[0]);
      var mLng = parseFloat(parts[1]);
      if (isNaN(mLat) || isNaN(mLng)) {
        clearMarker();
        return;
      }

      if (!isLngLatInsideRect({ lat: mLat, lng: mLng })) {
        clearMarker();
      }
    }

    function setMarkerIfInside(lngLat) {
      if (!hiddenMarker) return false;

      if (!isLngLatInsideRect(lngLat)) {
        // Not allowed -> remove marker if any
        clearMarker();
        return false;
      }

      hiddenMarker.value = lngLat.lat.toFixed(6) + "," + lngLat.lng.toFixed(6);
      updateMarkerPosition();
      buildThumbnailDebounced();
      return true;
    }

    function updateMarkerPosition() {
      if (!hiddenMarker || !hiddenMarker.value) {
        markerEl.style.display = "none";
        return;
      }

      var parts = hiddenMarker.value.split(",");
      if (parts.length !== 2) {
        markerEl.style.display = "none";
        return;
      }

      var mLat = parseFloat(parts[0]);
      var mLng = parseFloat(parts[1]);
      if (isNaN(mLat) || isNaN(mLng)) {
        markerEl.style.display = "none";
        return;
      }

      // If it ever ends outside the rectangle, remove it
      if (!isLngLatInsideRect({ lat: mLat, lng: mLng })) {
        clearMarker();
        return;
      }

      // pixel relative to the map's canvas container
      var pixel = map.project([mLng, mLat]);

      markerEl.style.left = pixel.x + "px";
      markerEl.style.top = pixel.y + "px";
      markerEl.style.display = "block";
    }

    // Build Mapbox Static image URL (for cart preview)
    function buildStaticThumbnailUrl() {
      if (!hiddenThumb) return;

      var c = map.getCenter();
      var centerLng = c.lng;
      var centerLat = c.lat;

      // Marker is optional (but only if valid and inside rect)
      var markerLng = null;
      var markerLat = null;

      if (hiddenMarker && hiddenMarker.value) {
        var parts = hiddenMarker.value.split(",");
        if (parts.length === 2) {
          var mLat = parseFloat(parts[0]);
          var mLng = parseFloat(parts[1]);
          if (!isNaN(mLat) && !isNaN(mLng) && isLngLatInsideRect({ lat: mLat, lng: mLng })) {
            markerLat = mLat;
            markerLng = mLng;
          }
        }
      }

      // Try to turn mapbox://styles/... into the static endpoint path
      var style = mapStyle;
      var stylePath = null;

      if (style.indexOf("mapbox://styles/") === 0) {
        stylePath = style.replace("mapbox://styles/", "");
      } else if (style.indexOf("https://api.mapbox.com/styles/v1/") === 0) {
        stylePath = style.replace("https://api.mapbox.com/styles/v1/", "").split("?")[0];
      } else {
        return;
      }

      // Size based on orientation (you can tweak later)
      var w = (orientation === "horizontal") ? 1000 : 600;
      var h = (orientation === "horizontal") ? 600 : 1000;

      w = clamp(w, 200, 1280);
      h = clamp(h, 200, 1280);

      var overlay = "";
      if (markerLng !== null && markerLat !== null) {
        overlay = "pin-s+e11d48(" + round(markerLng, 6) + "," + round(markerLat, 6) + ")";
      }

      var zoom = map.getZoom();
      var bearing = map.getBearing();
      var pitch = map.getPitch();

var b = getRectBounds();

var north = b.maxLat;
var south = b.minLat;
var east  = b.maxLng;
var west  = b.minLng;

var bbox = "[" + west + "," + south + "," + east + "," + north + "]";

      var url =
        "https://api.mapbox.com/styles/v1/" +
        stylePath +
        "/static/" +
        (overlay ? (overlay + "/") : "") +
        bbox +
        "/" +
        w + "x" + h +
        "@2x?access_token=" +
        encodeURIComponent(mapboxgl.accessToken) +
        "&logo=false&attribution=false";

      hiddenThumb.value = url;
    }

    var buildThumbnailDebounced = debounce(buildStaticThumbnailUrl, 250);

    function syncHidden() {
      if (!map || !map.isStyleLoaded()) return;

      var c = map.getCenter();
      if (hiddenCenter) {
        hiddenCenter.value = c.lat.toFixed(6) + "," + c.lng.toFixed(6);
      }

      var b = getRectBounds();

      if (hiddenBounds) {
        hiddenBounds.value =
          b.maxLat.toFixed(6) + "," + b.maxLng.toFixed(6) + ";" +
          b.minLat.toFixed(6) + "," + b.minLng.toFixed(6);
      }

      if (hiddenBlosm) {
        hiddenBlosm.value =
          b.minLng.toFixed(5) + ", " +
          b.minLat.toFixed(5) + ", " +
          b.maxLng.toFixed(5) + ", " +
          b.maxLat.toFixed(5);
      }

      if (hiddenZoom) {
        hiddenZoom.value = map.getZoom().toFixed(2);
      }

      // Keep Thumbnail URL updated for cart preview
      buildThumbnailDebounced();
    }

    function validate() {
      if (!map || !map.isStyleLoaded()) return;

      var b = getRectBounds();

      var midLat = (b.maxLat + b.minLat) / 2;
      var midLng = (b.maxLng + b.minLng) / 2;

      var km;
      if (orientation === "horizontal") {
        km = haversineKm(midLat, b.minLng, midLat, b.maxLng);
      } else {
        km = haversineKm(b.maxLat, midLng, b.minLat, midLng);
      }

      var valid = km >= minKm && km <= maxKm;

      rectEl.classList.toggle("map-selection-rect--valid", valid);
      rectEl.classList.toggle("map-selection-rect--invalid", !valid);
    }

    function applyOrientation(o) {
      orientation = (o === "horizontal") ? "horizontal" : "vertical";

      rectEl.classList.remove("map-selection-rect--vertical", "map-selection-rect--horizontal");
      rectEl.classList.add("map-selection-rect--" + orientation);

      if (labelEl) {
        labelEl.textContent = (orientation === "vertical") ? "Vertical" : "Horizontal";
      }

      if (hiddenOrientation) {
        hiddenOrientation.value = orientation;
      }

      var toggleLabel = sectionEl.querySelector(".product-map-builder__orientation-toggle-label");
      if (toggleLabel) {
        toggleLabel.textContent =
          (orientation === "vertical") ? "Horizontal (20×12)" : "Vertical (12×20)";
      }

      validate();
      syncHidden();
      buildThumbnailDebounced();

      // Enforce marker rule after orientation change (rectangle size changes)
      ensureMarkerWithinRect();
      updateMarkerPosition();
    }

    // ----------------------------
    // Geocoder
    // ----------------------------

    function attachGeocoder() {
      var searchContainer = sectionEl.querySelector(".product-map-builder__search");

      if (!searchContainer) {
        searchContainer = document.createElement("div");
        searchContainer.className = "product-map-builder__search";
        var topBar = sectionEl.querySelector(".product-map-builder__top-bar");
        if (topBar) {
          topBar.appendChild(searchContainer);
        } else {
          sectionEl.insertBefore(searchContainer, sectionEl.firstChild);
        }
      }

      function sizeSearchContainer() {
        if (window.innerWidth <= 768) {
          searchContainer.style.maxWidth = "100%";
          searchContainer.style.width = "100%";
          searchContainer.style.margin = "0";
        } else {
          searchContainer.style.maxWidth = "420px";
          searchContainer.style.width = "100%";
          searchContainer.style.margin = "0 auto";
        }
      }
      sizeSearchContainer();
      window.addEventListener("resize", sizeSearchContainer);

      // Preferred: MapboxGeocoder plugin
      if (typeof MapboxGeocoder !== "undefined") {
        var geocoder = new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl: mapboxgl,
          marker: false,
          placeholder: "Search for a place",
          zoom: 14
        });

        searchContainer.appendChild(geocoder.onAdd(map));

        geocoder.on("result", function (e) {
          var center = e.result && e.result.center ? e.result.center : null;
          if (!center) return; // [lng, lat]

          map.flyTo({
            center: center,
            zoom: 15,
            pitch: map.getPitch(),
            bearing: map.getBearing(),
            essential: true
          });

          if (hiddenCenter) hiddenCenter.value = center[1].toFixed(6) + "," + center[0].toFixed(6);

          // IMPORTANT: only set marker if inside rectangle
          if (hiddenMarker) {
            setMarkerIfInside({ lng: center[0], lat: center[1] });
          }

          validate();
          syncHidden();
          ensureMarkerWithinRect();
          updateMarkerPosition();
        });

        return;
      }

      // Fallback: simple HTML search using Mapbox Geocoding API
      var form = document.createElement("form");
      form.className = "product-map-builder__search-form";
      form.style.display = "flex";
      form.style.alignItems = "center";
      form.style.gap = "0.75rem";
      form.style.margin = "0";

      var input = document.createElement("input");
      input.type = "text";
      input.placeholder = "Search for a place";
      input.autocomplete = "off";
      input.className = "product-map-builder__search-input";

      input.style.flex = "1";
      input.style.boxSizing = "border-box";
      input.style.padding = "0.45rem 0.9rem";
      input.style.borderRadius = "9999px";
      input.style.border = "1px solid #000000ff";
      input.style.fontSize = "0.9rem";
      input.style.lineHeight = "1.3";
      input.style.background = "#ecececff";
      input.style.color = "#000000ff";
      input.style.boxShadow = "none";
      input.style.height = "auto";
      input.style.outline = "none";

      var button = document.createElement("button");
      button.type = "submit";
      button.textContent = "Search";
      button.className = "product-map-builder__search-button";

      button.style.borderRadius = "9999px";
      button.style.border = "1px solid #2b2b2bff";
      button.style.background = "#000000ff";
      button.style.color = "#ffffff";
      button.style.fontSize = "0.85rem";
      button.style.padding = "0.45rem 1rem";
      button.style.cursor = "pointer";
      button.style.fontWeight = "600";
      button.style.whiteSpace = "nowrap";
      button.style.boxShadow = "none";
      button.style.height = "auto";

      form.appendChild(input);
      form.appendChild(button);
      searchContainer.appendChild(form);

      function geocodeAndFly(query) {
        if (!query || !query.trim()) return;

        var url =
          "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
          encodeURIComponent(query) +
          ".json?access_token=" +
          encodeURIComponent(mapboxgl.accessToken) +
          "&limit=1";

        fetch(url)
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (!data.features || !data.features.length) return;
            var feat = data.features[0];
            var center = feat.center; // [lng, lat]

            map.flyTo({
              center: center,
              zoom: 15,
              pitch: map.getPitch(),
              bearing: map.getBearing(),
              essential: true
            });

            if (hiddenCenter) hiddenCenter.value = center[1].toFixed(6) + "," + center[0].toFixed(6);

            // IMPORTANT: only set marker if inside rectangle
            if (hiddenMarker) {
              setMarkerIfInside({ lng: center[0], lat: center[1] });
            }

            validate();
            syncHidden();
            ensureMarkerWithinRect();
            updateMarkerPosition();
          })
          .catch(function () {
            // silent
          });
      }

      form.addEventListener("submit", function (e) {
        e.preventDefault();
        geocodeAndFly(input.value);
      });
    }

    // ----------------------------
    // Events
    // ----------------------------

    if (toggleBtn) {
      toggleBtn.addEventListener("click", function () {
        var newOrientation = (orientation === "vertical") ? "horizontal" : "vertical";
        applyOrientation(newOrientation);
      });
    }

    // Click-to-place marker (ONLY inside rectangle)
    map.getCanvas().addEventListener("click", function (e) {
      if (!hiddenMarker) return;

      var r = map.getCanvas().getBoundingClientRect();
      var x = e.clientX - r.left;
      var y = e.clientY - r.top;

      var lngLat = map.unproject([x, y]);

      setMarkerIfInside(lngLat);

      validate();
      syncHidden();
      ensureMarkerWithinRect();
      updateMarkerPosition();
    });

    map.on("load", function () {
      applyOrientation(orientation);

      // If marker already exists (e.g., back button), enforce rule immediately
      ensureMarkerWithinRect();
      updateMarkerPosition();

      // Ensure inputs are populated before user adds to cart
      syncHidden();
      validate();

      // Ensure layout correct
      try { map.resize(); } catch (_) {}

      // Add search box
      attachGeocoder();
    });

    // Keep values updated during interaction
    ["move", "zoom", "moveend", "zoomend", "pitchend", "rotateend"].forEach(function (ev) {
      map.on(ev, function () {
        syncHidden();
        validate();

        // If marker exists but is now outside the rectangle, delete it
        ensureMarkerWithinRect();

        // If it still exists, keep it positioned correctly
        updateMarkerPosition();
      });
    });

    // ResizeObserver support
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () {
        try {
          map.resize();
          validate();
          syncHidden();

          ensureMarkerWithinRect();
          updateMarkerPosition();
        } catch (_) {}
      });
      ro.observe(mapContainer);
    }

    // Safety resizes
    setTimeout(function () {
      try { map.resize(); } catch (_) {}
      validate();
      syncHidden();
      ensureMarkerWithinRect();
      updateMarkerPosition();
    }, 300);

    setTimeout(function () {
      try { map.resize(); } catch (_) {}
      validate();
      syncHidden();
      ensureMarkerWithinRect();
      updateMarkerPosition();
    }, 800);
  }

  // DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Shopify section events
  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:select", initAll);
})();
